import type {
	AppID,
	NubeComponent,
	NubeComponentEventHandler,
	NubeComponentId,
	NubeComponentWithChildren,
	NubeSDK,
	NubeSDKListenableEvent,
	NubeSDKListener,
	UISlot,
} from "@tiendanube/nube-sdk-internal-types";
import deepcopy from "deepcopy";

type NubeComponentEventHandlerType = NubeComponentEventHandler<
	"change" | "blur" | "focus" | "click" | "increment" | "decrement",
	unknown
>;

type NubeComponentWithInternalId = NubeComponent & {
	__internalId?: string;
};

export type WorkerUISdk = {
	// Sets the component for a given slot.
	// Returns a copy of the received component with the internal ID set.
	setSlot(
		slot: UISlot,
		component: NubeComponent | NubeComponent[],
	): NubeComponent | NubeComponent[];

	getSlot(slot: UISlot): NubeComponent | NubeComponent[] | undefined;

	// Removes all components from the given slot.
	removeSlot(slot: UISlot): void;
};

type WorkerUIComponent = {
	__internalId: NubeComponentId;
	component: NubeComponent | NubeComponent[];
	children?: WorkerUIComponent[];
	clearFunctions?: (() => void)[];
};

function destroyComponent(component: WorkerUIComponent) {
	if (component.children) {
		component.children.forEach(destroyComponent);
	}
	if (component.clearFunctions) {
		for (const fn of component.clearFunctions) {
			fn();
		}
	}
}

function componentHasChildren(
	component: NubeComponent,
): component is NubeComponentWithChildren {
	return !!(component as NubeComponentWithChildren).children;
}

// Creates a new WorkerUIComponent from a NubeComponent or array of NubeComponents.
// It modifies the received component(s) to include internal IDs.
function createComponent(
	component: NubeComponent | NubeComponent[],
	sdk: NubeSDK,
	appId: AppID,
	internalComponentIdGenerator: () => string,
): WorkerUIComponent {
	if (Array.isArray(component)) {
		const internalId = internalComponentIdGenerator();
		const workerComponent: WorkerUIComponent = {
			__internalId: internalId,
			component: component,
			children: [],
		};

		for (const comp of component) {
			const compWithId = comp as NubeComponentWithInternalId;
			const compInternalId =
				compWithId.__internalId ?? internalComponentIdGenerator();
			compWithId.__internalId = compInternalId;
			setupComponentEventListeners(comp, workerComponent, compInternalId, sdk);

			if (componentHasChildren(compWithId) && compWithId.children) {
				const children = Array.isArray(compWithId.children)
					? compWithId.children
					: [compWithId.children];
				for (const child of children) {
					workerComponent.children?.push(
						createComponent(child, sdk, appId, internalComponentIdGenerator),
					);
				}
			}
		}

		return workerComponent;
	}

	const internalId =
		typeof component === "object" && component.__internalId
			? component.__internalId
			: internalComponentIdGenerator();

	if (typeof component === "object") {
		component.__internalId = internalId;
	}

	const workerComponent: WorkerUIComponent = {
		__internalId: internalId,
		component: component,
	};

	setupComponentEventListeners(component, workerComponent, internalId, sdk);

	if (componentHasChildren(component) && component.children) {
		workerComponent.children = Array.isArray(component.children)
			? component.children.map((child) => {
					return createComponent(
						child,
						sdk,
						appId,
						internalComponentIdGenerator,
					);
				})
			: [
					createComponent(
						component.children,
						sdk,
						appId,
						internalComponentIdGenerator,
					),
				];
	}

	return workerComponent;
}

function setupComponentEventListeners(
	component: NubeComponent,
	workerComponent: WorkerUIComponent,
	internalId: string,
	sdk: NubeSDK,
) {
	if (typeof component !== "object") return;

	const isHandlerComponent = [
		"field",
		"check",
		"txtarea",
		"dialog",
		"button",
		"select",
		"numberfield",
	].includes(component.type);

	if (isHandlerComponent) {
		const field = component as {
			type: string;
			onChange: NubeComponentEventHandlerType;
			onBlur: NubeComponentEventHandlerType;
			onFocus: NubeComponentEventHandlerType;
			onClose: NubeComponentEventHandlerType;
			onClick: NubeComponentEventHandlerType;
			onIncrement: NubeComponentEventHandlerType;
			onDecrement: NubeComponentEventHandlerType;
		};

		if (!workerComponent.clearFunctions) {
			workerComponent.clearFunctions = [];
		}

		const clearFunctions = workerComponent.clearFunctions;

		function addListener(
			eventHandler: NubeComponentEventHandlerType,
			type: "change" | "blur" | "focus" | "click" | "increment" | "decrement",
		) {
			const eventId: NubeSDKListenableEvent = `custom:${field.type}:${internalId}:${type}`;
			const listener: NubeSDKListener = (state) => {
				const value = state.ui.values[internalId];
				eventHandler({
					type,
					state,
					value,
				});
			};
			clearFunctions.push(() => sdk.off(eventId, listener));
			sdk.on(eventId, listener);
		}

		if (field.onChange) addListener(field.onChange, "change");
		if (field.onBlur) addListener(field.onBlur, "blur");
		if (field.onFocus) addListener(field.onFocus, "focus");
		if (field.onClose) addListener(field.onClose, "change");
		if (field.onClick) addListener(field.onClick, "click");
		if (field.onIncrement) addListener(field.onIncrement, "increment");
		if (field.onDecrement) addListener(field.onDecrement, "decrement");
	}
}

export function createWorkerUISdk(sdk: NubeSDK, appId: AppID): WorkerUISdk {
	const currentSlots: Partial<Record<UISlot, WorkerUIComponent>> = {};
	let lastComponentId = 0;

	function generateSequencialId(): string {
		return `${appId}-${lastComponentId++}`;
	}

	const uiSDK: WorkerUISdk = {
		setSlot(slot, component): NubeComponent | NubeComponent[] {
			// Remove the previous slot if it exists
			const currentSlot = currentSlots[slot];
			if (currentSlot) {
				delete currentSlots[slot];
				destroyComponent(currentSlot);
			}

			// Clone component before it is modified by createComponent()
			const clonedComponent = deepcopy(component);

			// Create new UI web worker components
			const newWorkerComponent = createComponent(
				clonedComponent,
				sdk,
				appId,
				generateSequencialId,
			);

			// Assign the slot
			currentSlots[slot] = newWorkerComponent;

			return clonedComponent;
		},
		getSlot(slot) {
			return currentSlots[slot]?.component;
		},
		removeSlot(slot) {
			const currentSlot = currentSlots[slot];
			if (currentSlot) {
				delete currentSlots[slot];
				destroyComponent(currentSlot);
			}
		},
	};

	return uiSDK;
}
